/**
 * In-app notification center + preferences — data hooks.
 *
 * relay.notifications rows are written by DB triggers (notify_hot_lead_from_score,
 * notify_deal_stage_change, notify_deal_creation) and surfaced here. Both
 * relay.notifications and relay.notification_preferences are RLS-scoped to
 * auth.uid(), so every query is implicitly the current user's; we still filter
 * by user_id client-side to keep query keys stable.
 *
 * No realtime subscription — the app doesn't use Supabase realtime anywhere
 * else, and the dispatcher cron runs every 2 minutes, so a 60s poll keeps the
 * bell fresh without adding a channel lifecycle.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Json } from "@/types/database";
import { EMAIL_REPORT_EVENTS, NOTIFICATION_EVENTS } from "./events";
import type {
  NotificationPreferences,
  NotificationPreferencesMap,
  RelayNotification,
} from "./types";

/** Most-recent notifications for the signed-in user (capped — pagination is a follow-up). */
const NOTIFICATION_LIMIT = 50;
/** Poll cadence — comfortably tighter than the 2-min dispatcher cron. */
const POLL_MS = 60_000;

export function useNotifications() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ["relay.notifications", userId],
    enabled: !!userId,
    refetchInterval: POLL_MS,
    staleTime: 30_000,
    queryFn: async (): Promise<RelayNotification[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .schema("relay")
        .from("notifications")
        .select(
          "id, event_type, title, message, contact_id, deal_id, priority, read_at, created_at",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(NOTIFICATION_LIMIT);
      if (error) throw error;
      // `priority` is a text column (CHECK-constrained, no enum) so it returns
      // as `string`; the app invariant narrows it to NotificationPriority.
      return (data ?? []) as RelayNotification[];
    },
  });
}

/** Unread count for the bell badge — a head-only COUNT, so it stays accurate past the list cap. */
export function useUnreadNotificationCount() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ["relay.notifications.unread", userId],
    enabled: !!userId,
    refetchInterval: POLL_MS,
    staleTime: 30_000,
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .schema("relay")
        .from("notifications")
        // GET (not HEAD) count: Supabase's edge intermittently 503s HEAD count
        // probes (origin returns 200), surfacing as failed requests on every
        // page load. A GET with limit(1) returns the exact count via
        // Content-Range without fetching rows, and doesn't trip the artifact.
        .select("id", { count: "exact" })
        .eq("user_id", userId)
        .is("read_at", null)
        .limit(1);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

function useNotificationKeys() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return {
    userId,
    invalidate: (qc: ReturnType<typeof useQueryClient>) => {
      qc.invalidateQueries({ queryKey: ["relay.notifications", userId] });
      qc.invalidateQueries({ queryKey: ["relay.notifications.unread", userId] });
    },
  };
}

/** Mark a single notification read. No-ops server-side if already read. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { invalidate } = useNotificationKeys();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .schema("relay")
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Mark every unread notification for the current user read. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { userId, invalidate } = useNotificationKeys();

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not authenticated");
      const { error } = await supabase
        .schema("relay")
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => invalidate(qc),
  });
}

/* ------------------------------------------------------------------ */
/*  Preferences                                                        */
/* ------------------------------------------------------------------ */

/** Effective defaults when a user has no notification_preferences row yet. */
function defaultPreferences(): NotificationPreferences {
  // Keep client-side defaults aligned with relay.notification_preferences defaults
  // from 20260427000900_create_relay_notifications.sql.
  const serverDefaultByEvent: Partial<NotificationPreferencesMap> = {
    hot_lead: { enabled: true, channels: ["email", "in_app"], priority: "urgent" },
    deal_created: { enabled: true, channels: ["in_app"], priority: "normal" },
    deal_stage_changed: { enabled: true, channels: ["in_app"], priority: "normal" },
    email_reply: { enabled: true, channels: ["email", "in_app"], priority: "high" },
    new_contact: { enabled: false, channels: ["in_app"], priority: "low" },
  };

  const preferences: NotificationPreferencesMap = {};
  for (const ev of NOTIFICATION_EVENTS) {
    preferences[ev.type] =
      serverDefaultByEvent[ev.type] ?? {
        // create_notification() defaults absent events to enabled + in_app.
        enabled: true,
        channels: ["in_app"],
        priority: "normal",
      };
  }
  for (const ev of EMAIL_REPORT_EVENTS) {
    // Email reports are opt-in: nothing is sent until the user enables the
    // toggle (the cron tick checks the STORED prefs, and no row = no email).
    preferences[ev.type] = { enabled: false, channels: ["email"], priority: "normal" };
  }
  return {
    preferences,
    quiet_hours_enabled: false,
    quiet_hours_start: "22:00",
    quiet_hours_end: "08:00",
    timezone: "America/New_York",
    digest_enabled: true,
    digest_frequency: "daily",
  };
}

/** Trim a Postgres TIME ("HH:MM:SS") down to the "HH:MM" an <input type=time> wants. */
function toHHMM(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  return value.slice(0, 5);
}

/**
 * Load the signed-in user's notification preferences, normalized so every one
 * of the nine event types is present (missing events resolve to the effective
 * default — `create_notification` treats an absent event as enabled/in_app).
 */
export function useNotificationPreferences() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ["relay.notification_preferences", userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<NotificationPreferences> => {
      const defaults = defaultPreferences();
      if (!userId) return defaults;

      const { data, error } = await supabase
        .schema("relay")
        .from("notification_preferences")
        .select(
          "preferences, quiet_hours_enabled, quiet_hours_start, quiet_hours_end, timezone, digest_enabled, digest_frequency",
        )
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return defaults;

      const stored = (data.preferences ?? {}) as unknown as NotificationPreferencesMap;
      // Normalize: fill any event missing from the stored map with its default.
      // Both catalogs must be carried — the upsert writes this map verbatim,
      // so a key left out here would be ERASED on the user's next save.
      // (The defaults hold every event of both catalogs, in this order; a stored
      // pref is jsonb data, never a function.)
      const preferences: NotificationPreferencesMap = { ...defaults.preferences };
      for (const ev of [...NOTIFICATION_EVENTS, ...EMAIL_REPORT_EVENTS]) {
        const pref = stored[ev.type];
        if (pref != null && typeof pref !== "function") preferences[ev.type] = pref;
      }

      return {
        preferences,
        quiet_hours_enabled: data.quiet_hours_enabled ?? false,
        quiet_hours_start: toHHMM(data.quiet_hours_start, "22:00"),
        quiet_hours_end: toHHMM(data.quiet_hours_end, "08:00"),
        timezone: data.timezone ?? "America/New_York",
        digest_enabled: data.digest_enabled ?? true,
        digest_frequency:
          (data.digest_frequency as NotificationPreferences["digest_frequency"]) ?? "daily",
      };
    },
  });
}

/** Upsert the signed-in user's notification preferences (one row per user_id). */
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useMutation({
    mutationFn: async (prefs: NotificationPreferences) => {
      if (!userId) throw new Error("Not authenticated");
      const { error } = await supabase
        .schema("relay")
        .from("notification_preferences")
        .upsert(
          {
            user_id: userId,
            preferences: prefs.preferences as unknown as Json,
            quiet_hours_enabled: prefs.quiet_hours_enabled,
            quiet_hours_start: prefs.quiet_hours_start,
            quiet_hours_end: prefs.quiet_hours_end,
            timezone: prefs.timezone,
            digest_enabled: prefs.digest_enabled,
            digest_frequency: prefs.digest_frequency,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["relay.notification_preferences", userId],
      });
    },
  });
}
